import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, TextField, CircularProgress, Alert, Stack,
    InputAdornment, Breadcrumbs, Link, Chip, Tooltip, Avatar, MenuItem, Divider,
    Grid, Card, CardContent, LinearProgress, Tab, Tabs, Drawer,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
    Search as SearchIcon,
    TrendingUp,
    Assessment,
    Edit as EditIcon,
    FilterList,
    Save as SaveIcon,
    History as HistoryIcon,
    Description,
    Download as DownloadIcon,
    Print as PrintIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, getUploadUrl } from '../config/api';
import { getLetterGrade, getGradeInfo } from '../utils/gradeUtils';
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
    ResponsiveContainer, Cell, CartesianGrid
} from 'recharts';

const GradesPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Metadata
    const [subjects, setSubjects] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [exams, setExams] = useState<any[]>([]);
    const [students, setStudents] = useState<any[]>([]);

    // Filters
    const [filters, setFilters] = useState({
        classGroupId: '',
        subjectId: '',
        examId: '',
        term: 'Term 1',
        academicYear: new Date().getFullYear()
    });

    // Markbook Data
    const [localMarks, setLocalMarks] = useState<Record<string, { marks: number | string, comment: string, changed: boolean, existingId?: string | null }>>({});
    const [searchTerm, setSearchTerm] = useState('');

    // Drawer & Dialogs
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [selectedStudentHistory, setSelectedStudentHistory] = useState<any>(null);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [selectedStudentReport, setSelectedStudentReport] = useState<any>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [school, setSchool] = useState<any>(null);

    // Printing Setup
    const reportRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: reportRef,
        documentTitle: `Report_Card_${reportData?.summary?.studentId || 'Student'}`
    });

    // Download PDF logic
    const handleDownloadPDF = async () => {
        if (!reportRef.current) return;
        try {
            const canvas = await html2canvas(reportRef.current, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Report_Card_${reportData?.summary?.studentId}.pdf`);
        } catch (err) {
            console.error('PDF Generation failed:', err);
        }
    };

    useEffect(() => {
        const fetchSchool = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(getApiUrl('school'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSchool(data);
            } catch (err) {
                console.error('Error fetching school profile:', err);
            }
        };
        fetchSchool();
    }, []);


    // Term Summary Data
    const [termReports, setTermReports] = useState<Record<string, any>>({});

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // 1. Fetch metadata in parallel
            const [subjRes, groupRes, examRes] = await Promise.all([
                axios.get(getApiUrl('subjects'), config),
                axios.get(getApiUrl('groups/my'), config),
                axios.get(getApiUrl('exams'), config)
            ]);

            setSubjects(subjRes.data);
            setGroups(groupRes.data);
            setExams(examRes.data);

            // Auto-select first group/subject functional updates
            setFilters(prev => {
                const newFilters = { ...prev };
                if (!newFilters.classGroupId && groupRes.data.length > 0) {
                    newFilters.classGroupId = groupRes.data[0].id;
                }
                if (!newFilters.subjectId && subjRes.data.length > 0) {
                    newFilters.subjectId = subjRes.data[0].id;
                }
                if (!newFilters.examId && examRes.data.length > 0) {
                    newFilters.examId = examRes.data[0].id;
                }
                return newFilters;
            });
        } catch (err: any) {
            console.error('Initial fetch failed:', err);
            setError('Failed to load metadata');
        } finally {
            setLoading(false);
        }
    }, []); // Removed filters dependencies

    useEffect(() => { fetchData(); }, [fetchData]);

    const fetchGrades = useCallback(async () => {
        if (!filters.classGroupId) return;
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const [gradeRes, studentRes, termReportRes] = await Promise.all([
                axios.get(getApiUrl('grades'), {
                    params: { ...filters, subjectId: activeTab === 0 ? filters.subjectId : undefined },
                    ...config
                }),
                axios.get(getApiUrl(`groups/${filters.classGroupId}`), config),
                axios.get(getApiUrl('academic-reports/bulk'), {
                    params: {
                        term: filters.term,
                        academicYear: filters.academicYear,
                        classGroupId: filters.classGroupId
                    },
                    ...config
                })
            ]);

            const classStudents = studentRes.data.students || [];
            setStudents(classStudents);

            // Initialize local marks
            const markMap: any = {};
            classStudents.forEach((s: any) => {
                const existing = gradeRes.data.find((g: any) => g.studentId === s.id);
                markMap[s.id] = {
                    marks: existing ? existing.marks : '',
                    comment: existing ? existing.comment : '',
                    changed: false,
                    existingId: existing ? existing.id : null
                };
            });
            setLocalMarks(markMap);

            // Initialize term reports
            const reportMap: any = {};
            termReportRes.data.forEach((r: any) => {
                reportMap[r.studentId] = r;
            });
            setTermReports(reportMap);

        } catch (err: any) {
            console.error('Fetch grades failed:', err);
            setError('Failed to load class records');
        } finally {
            setLoading(false);
        }
    }, [filters, activeTab]);

    // Fetch students and existing grades when filters change
    useEffect(() => {
        if (filters.classGroupId) fetchGrades();
    }, [filters.classGroupId, fetchGrades]);

    const handleMarkChange = (studentId: string, field: 'marks' | 'comment', value: string | number) => {
        setLocalMarks(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value,
                changed: true
            }
        }));
    };

    const handleSaveGrades = async () => {
        try {
            setSaving(true);
            setError('');
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const selectedExam = exams.find(e => e.id === filters.examId);

            // Prepare payload of only changed marks
            const gradesToSave = Object.entries(localMarks)
                .filter(([_, data]) => data.changed && data.marks !== '')
                .map(([studentId, data]) => ({
                    id: data.existingId,
                    studentId,
                    marks: Number(data.marks),
                    maxMarks: 100, // Default for now
                    comment: data.comment
                }));

            if (gradesToSave.length === 0) {
                setSuccess('No changes to save.');
                return;
            }

            await axios.post(getApiUrl('grades/bulk'), {
                subjectId: filters.subjectId,
                examType: selectedExam?.examType || 'General',
                term: filters.term,
                academicYear: filters.academicYear,
                grades: gradesToSave
            }, config);

            setSuccess(`${gradesToSave.length} grades updated successfully!`);
            // Mark all as saved
            setLocalMarks(prev => {
                const updated = { ...prev };
                gradesToSave.forEach(g => {
                    if (updated[g.studentId]) updated[g.studentId].changed = false;
                });
                return updated;
            });

            // Clear success message after 3s
            setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save grades');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTermReport = async (studentId: string) => {
        const report = termReports[studentId] || {};
        try {
            // setSavingTermReport(studentId); // Removed unused state
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            await axios.post(getApiUrl('academic-reports'), {
                studentId,
                term: filters.term,
                academicYear: filters.academicYear,
                ...report
            }, config);

            setSuccess('Term summary saved for student');
        } catch (err: any) {
            setError('Failed to save term report');
        } finally {
            // setSavingTermReport(null); // Removed unused state
        }
    };

    const handleTermReportChange = (studentId: string, field: string, value: any) => {
        if (studentId === 'GLOBAL_NEXT_TERM') {
            setTermReports(prev => {
                const updated = { ...prev };
                // Update the global value
                updated['GLOBAL_NEXT_TERM'] = { ...updated['GLOBAL_NEXT_TERM'], [field]: value };
                
                // Also update all students that are currently listed
                filteredStudents.forEach(s => {
                    updated[s.id] = { 
                        ...(updated[s.id] || {}), 
                        nextTermDate: value 
                    };
                });
                return updated;
            });
            return;
        }

        setTermReports(prev => ({
            ...prev,
            [studentId]: {
                ...(prev[studentId] || {}),
                [field]: value
            }
        }));
    };

    const handleViewReportCard = async (student: any) => {
        try {
            setSelectedStudentReport(student);
            setReportDialogOpen(true);
            setLoadingReport(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const res = await axios.get(getApiUrl(`grades/report-card/${student.id}`), {
                params: { term: filters.term, academicYear: filters.academicYear },
                ...config
            });
            setReportData(res.data);
        } catch (err) {
            setError('Failed to load report card data');
        } finally {
            setLoadingReport(false);
        }
    };


    const filteredStudents = students.filter(s =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.studentId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Chart Data
    const selectedGroup = groups.find(g => g.id === filters.classGroupId);
    const currentGrade = selectedGroup?.grade || '';

    const gradeStats = Object.values(localMarks).reduce((acc: any, curr: any) => {
        if (curr.marks === '') return acc;
        const result = getGradeInfo(Number(curr.marks), currentGrade);
        acc[result.label] = (acc[result.label] || 0) + 1;
        return acc;
    }, {});

    const chartData = ['A+', 'A', 'B', 'C', 'D', 'F'].map(label => ({
        name: label,
        students: gradeStats[label] || 0
    }));

    const averageMark = Object.values(localMarks).filter(m => m.marks !== '').length > 0
        ? Math.round(Object.values(localMarks).filter(m => m.marks !== '').reduce((sum, curr) => sum + Number(curr.marks), 0) / Object.values(localMarks).filter(m => m.marks !== '').length)
        : 0;

    return (
        <Box sx={{ p: 4, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard/academics')} sx={{ cursor: 'pointer' }}>Academics</Link>
                    <Typography color="text.primary">Markbook</Typography>
                </Breadcrumbs>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em' }}>
                                Markbook Control
                            </Typography>
                            <Chip label={filters.term} sx={{ fontWeight: 800, bgcolor: '#EBF4FF', color: '#4C51BF' }} />
                        </Stack>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                            High-density grade entry and academic performance analytics.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button startIcon={<DownloadIcon />} sx={{ borderRadius: '12px', fontWeight: 700, px: 3, border: '1px solid #E2E8F0', bgcolor: 'white', color: '#4A5568' }}>Export sheet</Button>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            disabled={saving}
                            onClick={handleSaveGrades}
                            sx={{ bgcolor: '#263B7C', borderRadius: '12px', px: 4, fontWeight: 800, boxShadow: '0 4px 14px rgba(38,59,124,0.25)', '&:hover': { bgcolor: '#1e2e60' } }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {/* Filter Ribbons */}
            <Paper sx={{ p: 2, borderRadius: '20px', mb: 4, border: '1px solid #edf2f7', bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            select fullWidth size="small" label="Class Group"
                            value={filters.classGroupId}
                            onChange={(e) => setFilters(prev => ({ ...prev, classGroupId: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name} ({g.grade})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            select fullWidth size="small" label="Subject"
                            value={filters.subjectId}
                            onChange={(e) => setFilters(prev => ({ ...prev, subjectId: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            select fullWidth size="small" label="Assessment / Exam"
                            value={filters.examId}
                            onChange={(e) => setFilters(prev => ({ ...prev, examId: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.name} ({e.examType})</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                select fullWidth size="small" label="Term"
                                value={filters.term}
                                onChange={(e) => setFilters(prev => ({ ...prev, term: e.target.value }))}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            >
                                <MenuItem value="Term 1">Term 1</MenuItem>
                                <MenuItem value="Term 2">Term 2</MenuItem>
                                <MenuItem value="Term 3">Term 3</MenuItem>
                            </TextField>
                            <Button startIcon={<FilterList />} sx={{ minWidth: 44, height: 40, p: 0, borderRadius: '10px', bgcolor: '#F7FAFC', border: '1px solid #E2E8F0' }} />
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px', fontWeight: 700 }} icon={<CheckCircleIcon />}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontWeight: 700 }}>{error}</Alert>}

            {/* Content Tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ mb: 3, borderBottom: '1px solid #E2E8F0' }}
                TabIndicatorProps={{ sx: { height: 4, borderRadius: '4px 4px 0 0', bgcolor: '#263B7C' } }}
            >
                <Tab label="MARK ENTRY GRID" icon={<EditIcon />} iconPosition="start" sx={{ fontWeight: 800 }} />
                <Tab label="CLASS ANALYTICS" icon={<Assessment />} iconPosition="start" sx={{ fontWeight: 800 }} />
                <Tab label="TERM SUMMARY & HOD COMMENTS" icon={<Description />} iconPosition="start" sx={{ fontWeight: 800 }} />
            </Tabs>

            {activeTab === 0 && (
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ borderRadius: '24px', border: '1px solid #edf2f7', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <TextField
                                    placeholder="Search by name or ID..."
                                    size="small"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20 }} /></InputAdornment> }}
                                />
                                <Typography variant="caption" fontWeight={800} color="text.secondary">
                                    {Object.values(localMarks).filter(m => m.changed).length} UNSAVED CHANGES
                                </Typography>
                            </Box>

                            <TableContainer sx={{ maxHeight: '60vh' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem', width: 60 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Student</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem', width: 140 }}>Score / 100</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem', width: 100 }}>Grade</TableCell>
                                            <TableCell sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Comments</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: '#F8FAFC', color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem', width: 100 }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                                        ) : filteredStudents.length === 0 ? (
                                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 10, color: 'text.secondary' }}>No students found in this class.</TableCell></TableRow>
                                        ) : filteredStudents.map((s, index) => {
                                            const mark = localMarks[s.id] || { marks: '', comment: '', changed: false };
                                            const lGrade = mark.marks !== '' ? getGradeInfo(Number(mark.marks), s.grade) : null;
                                            return (
                                                <TableRow key={s.id} sx={{ bgcolor: mark.changed ? '#FFFAF0' : 'white', transition: 'background-color 0.2s' }}>
                                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#EBF4FF', color: '#4C51BF', fontSize: '0.8rem', fontWeight: 800 }}>
                                                                {s.firstName[0]}{s.lastName[0]}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={800}>{s.firstName} {s.lastName}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{s.studentId}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={mark.marks}
                                                            onChange={(e) => handleMarkChange(s.id, 'marks', e.target.value)}
                                                            InputProps={{ inputProps: { min: 0, max: 100 }, sx: { fontWeight: 900, textAlign: 'center' } }}
                                                            sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {lGrade ? (
                                                            <Chip
                                                                label={lGrade.label}
                                                                size="small"
                                                                sx={{ fontWeight: 900, color: 'white', bgcolor: lGrade.color, borderRadius: '6px', minWidth: 40 }}
                                                            />
                                                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            fullWidth size="small"
                                                            placeholder="Add internal feedback..."
                                                            value={mark.comment}
                                                            onChange={(e) => handleMarkChange(s.id, 'comment', e.target.value)}
                                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="View Student History">
                                                            <IconButton size="small" onClick={() => { setSelectedStudentHistory(s); setHistoryDrawerOpen(true); }}>
                                                                <HistoryIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 1 && (
                <Grid container spacing={4}>
                    {/* Performance Overview Cards */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Class Average</Typography>
                                <Stack direction="row" spacing={2} alignItems="center" mt={1} mb={3}>
                                    <Typography variant="h2" fontWeight={900} color="#263B7C">{averageMark}%</Typography>
                                    <Box sx={{ bgcolor: '#C6F6D5', color: '#22543D', px: 1, py: 0.5, borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                                        <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                                        <Typography variant="caption" fontWeight={800}>+4.2%</Typography>
                                    </Box>
                                </Stack>
                                <Divider sx={{ mb: 3 }} />
                                <Stack spacing={2.5}>
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" mb={1}>
                                            <Typography variant="body2" fontWeight={700}>Completion Progress</Typography>
                                            <Typography variant="body2" fontWeight={800}>{Math.round((Object.values(localMarks).filter((m: any) => m.marks !== '').length / students.length) * 100)}%</Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(Object.values(localMarks).filter((m: any) => m.marks !== '').length / students.length) * 100}
                                            sx={{ height: 10, borderRadius: '5px', bgcolor: '#EBF4FF', '& .MuiLinearProgress-bar': { bgcolor: '#4C51BF' } }}
                                        />
                                    </Box>
                                    <Stack direction="row" spacing={3}>
                                        <Box>
                                            <Typography variant="h5" fontWeight={800}>{students.length}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Students</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="h5" fontWeight={800}>{Object.values(localMarks).filter((m: any) => m.marks !== '').length}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Graded</Typography>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Grade Distribution Chart */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Paper sx={{ p: 4, borderRadius: '24px', border: '1px solid #edf2f7', height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                            <Typography variant="h6" fontWeight={800} color="#2D3748" mb={3}>Grade Distribution</Typography>
                            <Box sx={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#718096', fontWeight: 700 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#718096', fontWeight: 700 }} />
                                        <RechartsTooltip
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                            cursor={{ fill: '#F7FAFC' }}
                                        />
                                        <Bar dataKey="students" radius={[8, 8, 0, 0]} barSize={50}>
                                            {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={['#4C51BF', '#3182CE', '#38A169', '#D69E2E', '#E53E3E'][index % 5]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 2 && (
                <Box>
                    <Paper sx={{ p: 4, borderRadius: '24px', border: '1px solid #edf2f7', mb: 4, bgcolor: '#F4F7FE' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="h5" fontWeight={800} color="#263B7C">Final Term Assessments</Typography>
                                <Typography variant="body2" color="text.secondary">Enter high-level comments for report cards.</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                    label="Set Global Next Term Date"
                                    type="date"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ bgcolor: 'white', borderRadius: '8px', minWidth: 200 }}
                                    value={termReports['GLOBAL_NEXT_TERM']?.date || ''}
                                    onChange={(e) => handleTermReportChange('GLOBAL_NEXT_TERM', 'date', e.target.value)}
                                    helperText="Applies to all students below"
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ bgcolor: '#263B7C', borderRadius: '8px', fontWeight: 700, px: 3 }}
                                    onClick={async () => {
                                        const globalDate = termReports['GLOBAL_NEXT_TERM']?.date;
                                        if (!globalDate) return alert('Please select a date first');
                                        
                                        // Save for all filtered students
                                        setSaving(true);
                                        try {
                                             const userInfo = localStorage.getItem('userInfo');
                                             const token = userInfo ? JSON.parse(userInfo).token : null;
                                             const config = { headers: { Authorization: `Bearer ${token}` } };
                                             
                                             await Promise.all(filteredStudents.map(s => 
                                                 axios.post(getApiUrl('academic-reports'), {
                                                     studentId: s.id,
                                                     term: filters.term,
                                                     academicYear: filters.academicYear,
                                                     ...termReports[s.id],
                                                     nextTermDate: globalDate
                                                 }, config)
                                             ));
                                             setSuccess('Global term date applied and saved for all students!');
                                        } catch (err) {
                                            setError('Failed to save global date');
                                        } finally {
                                            setSaving(false);
                                        }
                                    }}
                                >
                                    APPLY & SAVE ALL
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>

                    <Grid container spacing={3}>
                        {filteredStudents.map((s) => {
                            const report = termReports[s.id] || {};
                            return (
                                <Grid size={{ xs: 12 }} key={s.id}>
                                    <Paper sx={{ p: 3, borderRadius: '20px', border: '1px solid #edf2f7', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                                        <Grid container spacing={3} alignItems="center">
                                            <Grid size={{ xs: 12, md: 3 }}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Avatar sx={{ width: 48, height: 48, bgcolor: '#263B7C' }}>
                                                        {s.firstName[0]}{s.lastName[0]}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h6" fontWeight={800}>{s.firstName} {s.lastName}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{s.studentId}</Typography>
                                                        <Button
                                                            size="small"
                                                            startIcon={<Description />}
                                                            sx={{ mt: 1, color: '#263B7C', fontWeight: 700 }}
                                                            onClick={() => handleViewReportCard(s)}
                                                        >
                                                            PREVIEW REPORT
                                                        </Button>
                                                    </Box>
                                                </Stack>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth multiline rows={2}
                                                    label="Teacher's Comment"
                                                    value={report.teacherComment || ''}
                                                    onChange={(e) => handleTermReportChange(s.id, 'teacherComment', e.target.value)}
                                                    variant="outlined" size="small"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 3 }}>
                                                <TextField
                                                    fullWidth multiline rows={2}
                                                    label="HOD's Comment"
                                                    value={report.hodComment || ''}
                                                    onChange={(e) => handleTermReportChange(s.id, 'hodComment', e.target.value)}
                                                    variant="outlined" size="small"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 3 }}>
                                                <Stack spacing={1}>
                                                    <TextField
                                                        fullWidth multiline rows={2}
                                                        label="Principal / Head Remarks"
                                                        value={report.principalComment || ''}
                                                        onChange={(e) => handleTermReportChange(s.id, 'principalComment', e.target.value)}
                                                        variant="outlined" size="small"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', mb: 1 } }}
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        label="Next Term Reopens"
                                                        type="date"
                                                        value={report.nextTermDate ? new Date(report.nextTermDate).toISOString().split('T')[0] : ''}
                                                        onChange={(e) => handleTermReportChange(s.id, 'nextTermDate', e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                        variant="outlined" size="small"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, bgcolor: '#F0FFF4' }}
                                                        helperText="Overrides global date"
                                                    />
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ bgcolor: '#48BB78', borderRadius: '8px', fontWeight: 700 }}
                                                            onClick={() => handleSaveTermReport(s.id)}
                                                        >
                                                            SAVE
                                                        </Button>
                                                    </Box>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {/* History Drawer */}
            <Drawer
                anchor="right"
                open={historyDrawerOpen}
                onClose={() => setHistoryDrawerOpen(false)}
                PaperProps={{ sx: { width: 400, p: 4, bgcolor: '#F7FAFC' } }}
            >
                {selectedStudentHistory && (
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" mb={4}>
                            <Avatar sx={{ width: 56, height: 56, bgcolor: '#4C51BF', fontWeight: 800 }}>
                                {selectedStudentHistory.firstName[0]}{selectedStudentHistory.lastName[0]}
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={800}>{selectedStudentHistory.firstName} {selectedStudentHistory.lastName}</Typography>
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>Performance History</Typography>
                            </Box>
                        </Stack>

                        <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Subject: {subjects.find(s => s.id === filters.subjectId)?.name}</Typography>

                        <Stack spacing={2} mt={2}>
                            {[
                                { term: 'Term 3', year: '2023', exam: 'Finals', score: 88, grade: 'A' },
                                { term: 'Term 2', year: '2023', exam: 'Mid-term', score: 76, grade: 'B' },
                                { term: 'Term 1', year: '2023', exam: 'Mock', score: 64, grade: 'C' },
                            ].map((h, i) => (
                                <Paper key={i} sx={{ p: 2, borderRadius: '16px', border: '1px solid #edf2f7' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={800}>{h.term} {h.year}</Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>{h.exam}</Typography>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="h6" fontWeight={900} color="#263B7C">{h.score}%</Typography>
                                            <Chip label={h.grade} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 900, bgcolor: '#EBF4FF' }} />
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>

                        <Alert severity="info" sx={{ mt: 4, borderRadius: '14px', '& .MuiAlert-message': { fontWeight: 600 } }}>
                            History data is pulled from previous academic terms recorded in the system.
                        </Alert>
                    </Box>
                )}
            </Drawer>
            {/* Report Card Dialog */}
            <Dialog
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '24px', overflow: 'hidden' }
                }}
            >
                <style>
                    {`
                        @media print {
                            body * { visibility: hidden; }
                            #report-card-printable, #report-card-printable * { visibility: visible; }
                            #report-card-printable {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                padding: 0;
                                margin: 0;
                                background: white !important;
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                            .no-print { display: none !important; }
                            .MuiPaper-root { border: 1px solid #edf2f7 !important; box-shadow: none !important; }
                            @page { size: auto; margin: 15mm; }
                        }
                    `}
                </style>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white', p: 3 }} className="no-print">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={900}>OFFICIAL STUDENT REPORT CARD</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Academic Year {filters.academicYear} - {filters.term}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button startIcon={<PrintIcon />} sx={{ color: 'white' }} onClick={() => handlePrint()}>Print</Button>
                            <IconButton onClick={() => setReportDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {loadingReport ? (
                        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                    ) : reportData ? (
                        <Box ref={reportRef} id="report-card-printable" sx={{ bgcolor: 'white', pb: 10 }}>
                            {/* Professional Branded Header */}
                            <Grid container spacing={4} sx={{ mb: 6, pb: 4, borderBottom: '2px solid #263B7C' }}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack direction="row" spacing={3} alignItems="center">
                                        {school?.logo && (
                                            <Box
                                                component="img"
                                                src={getUploadUrl(school.logo)}
                                                sx={{ height: 100, width: 100, objectFit: 'contain' }}
                                            />
                                        )}
                                        <Box>
                                            <Typography variant="h5" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                                                {school?.name || 'SquareEdge Academy'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ maxWidth: 400, lineHeight: 1.4 }}>
                                                {school?.address}
                                                {'\n'}{school?.phone} | {school?.email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }} sx={{ textAlign: 'right' }}>
                                    <Typography variant="h4" fontWeight={900} color="#F1F4F9" sx={{ letterSpacing: 2, mb: -1, opacity: 0.5 }}>
                                        REPORT CARD
                                    </Typography>
                                    <Typography variant="h6" fontWeight={900} color="#263B7C">
                                        TERM END RESULTS
                                    </Typography>
                                    <Typography variant="body2" fontWeight={800} color="#F9B43F">
                                        {filters.academicYear} | {filters.term}
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Student Header */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#F8FAFC' }}>
                                        <Stack direction="row" spacing={3} alignItems="center">
                                            <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: '#263B7C' }}>
                                                {selectedStudentReport?.firstName[0]}{selectedStudentReport?.lastName[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" fontWeight={900} color="#2D3748">
                                                    {selectedStudentReport?.firstName} {selectedStudentReport?.lastName}
                                                </Typography>
                                                <Typography variant="subtitle2" color="#4A5568" fontWeight={700}>ID: {selectedStudentReport?.studentId}</Typography>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Grade: {selectedStudentReport?.grade} | Class: {selectedStudentReport?.class}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ p: 3, textAlign: 'right' }}>
                                        <Typography variant="h4" fontWeight={900} color="#263B7C">{reportData.summary.overallPercentage}%</Typography>
                                        <Typography variant="subtitle1" fontWeight={900} color={reportData.summary.overallPercentage >= 50 ? '#48BB78' : '#E53E3E'}>
                                            GRADE: {reportData.summary.grade}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={800} color="#4A5568">
                                            RANK: {reportData.summary.rank} of {reportData.summary.classSize}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Performance Score</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Grades Table */}
                            <Typography variant="h6" fontWeight={800} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assessment color="primary" /> ACADEMIC TRANSCRIPT
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', mb: 4 }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>SUBJECT</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>RAW SCORE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>PERCENTAGE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>GRADE</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>TEACHER FEEDBACK</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.grades.map((g: any) => (
                                            <TableRow key={g.id}>
                                                <TableCell sx={{ fontWeight: 700 }}>{g.subject.name}</TableCell>
                                                <TableCell align="center">{g.marks} / {g.maxMarks}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: '#EBF4FF', color: '#4C51BF', fontWeight: 800 }}>
                                                        {((Number(g.marks) / Number(g.maxMarks)) * 100).toFixed(0)}%
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 900 }}>
                                                    {getLetterGrade((g.marks / g.maxMarks) * 100, selectedStudentReport?.grade)}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                                    "{g.comment || 'No comment provided'}"
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Remarks Section */}
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1, textTransform: 'uppercase', color: '#263B7C', letterSpacing: 1 }}>Official Remarks</Typography>
                                    <Stack spacing={2}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                                            <Typography variant="caption" fontWeight={800} color="#263B7C">TEACHER'S GENERAL COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.teacherComment || 'Pending assessment'}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#FFF5F5' }}>
                                            <Typography variant="caption" fontWeight={800} color="#E53E3E">HOD'S COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.hodComment || 'Pending review'}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#F0FFF4' }}>
                                            <Typography variant="caption" fontWeight={800} color="#38A169">PRINCIPAL'S COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.principalComment || 'Pending sign-off'}</Typography>
                                        </Paper>
                                    </Stack>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ my: 3 }} />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={800}>Attendance Standing</Typography>
                                            <Typography variant="h5" fontWeight={900} color="#263B7C">{reportData.attendance.percentage}%</Typography>
                                            <Typography variant="caption" color="text.secondary">{reportData.attendance.PRESENT} Days Present</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 200, mt: 4 }}>
                                            <Divider sx={{ mb: 1, borderColor: '#263B7C', borderBottomWidth: 2 }} />
                                            <Typography variant="caption" fontWeight={800} color="text.secondary">SCHOOL STAMP & PRINCIPAL'S SIGNATURE</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="subtitle2" fontWeight={800}>Next Term Information</Typography>
                                            <Typography variant="h5" fontWeight={900} color="#ED8936">
                                                {reportData.termReport?.nextTermDate ? new Date(reportData.termReport.nextTermDate).toLocaleDateString() : 'TBA'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">SCHOOL REOPENS</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 10, pt: 4, borderTop: '1px solid #edf2f7', textAlign: 'center', opacity: 0.5 }}>
                                <Typography variant="caption">
                                    This is an official document generated by {school?.name || 'the School Management System'}. Any alteration renders it void.
                                    Generated on {new Date().toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography align="center" color="text.secondary">No report card data found.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#F8FAFC' }} className="no-print">
                    <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<DownloadIcon />} 
                        sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                        onClick={handleDownloadPDF}
                    >
                        Download PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default GradesPage;
